import { Suspense, useCallback, useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { lazyWithRetry } from '@/shared/lib/lazy-with-retry';
import { RouteErrorBoundary } from '@/app/route-error-boundary';
import { SideNav } from '@/components/blocks/side-nav';
import { TopBar } from '@/app/layout/top-bar';
import { SchedulingPage } from '@/scheduling/pages/scheduling-page';
import { CreateAppointmentPanel } from '@/scheduling/components/appointments/create-appointment-panel';
import { useAuthStore } from '@/auth/auth-store';
import { ORG_ADMIN_ROLE, SCRIPS_ADMIN_ROLE } from '@/auth/roles';
import {
  readLastNav,
  writeLastNav,
  hasRootDeepLink,
  shouldRestoreLastNav,
  stripRootDeepLinkParams,
  type NavId,
} from '@/app/lib/last-selected-nav';
import {
  allowsNavId,
  confinedRedirectPath,
  isNavConfined,
} from '@/app/lib/nav-confinement';
import { railActiveId } from '@/app/lib/rail-active';
import { useForceLogout } from '@/auth/use-force-logout';
import { LoginRedirect } from '@/auth/login-redirect';
import { ForceLogoutDialog } from '@/auth/force-logout-dialog';
import { OnboardingWizard } from '@/onboarding/onboarding-wizard';
import { useUnreadNotificationCount } from '@/notifications/services/use-unread-notification-count';
import { useNotificationCountRealtime } from '@/notifications/services/use-notification-count-realtime';
import { IconButtonTooltips } from '@/components/ui/tooltip';
import { NotificationPanel } from '@/notifications/components/notification-panel';
import { useNotificationPanelStore } from '@/notifications/store/notification-panel-store';
import { bookedToSaveSuccess, useCreateAppointmentHandoff } from '@/shared/store/create-appointment-handoff';
import { useNavGuard } from '@/shared/store/nav-guard';
import { UnsavedChangesGuardDialog } from '@/app/layout/unsaved-changes-guard-dialog';
import { inboxNotifyEnabled } from '@/scheduling/inbox/notify/inbox-notify-flag';
import { inboxThreadPath } from '@/scheduling/inbox/notify/inbox-notify-text';
import { requestInboxThread } from '@/scheduling/inbox/notify/inbox-open-thread';
import { useInboxNewMessageNotify } from '@/scheduling/inbox/notify/use-inbox-new-message-notify';
import { useInboxUnreadTotal } from '@/scheduling/inbox/services/use-inbox-unread-total';
import { useStaffChatShell } from '@/staff-chat/use-staff-chat-shell';
import { StaffChatDashboardSlot, StaffChatDockSlot } from '@/staff-chat/components/staff-chat-dashboard-slot';


// `lazyWithRetry` (not bare `React.lazy`) so a stale chunk after a deploy
// auto-reloads once instead of white-screening. See `lazy-with-retry.ts`.
const BillingRoutes = lazyWithRetry(() => import('@/billing/billing-routes'), 'billing');
// Inbox is lazy for PAYLOAD fail-closure, not just behavioural (#836 review
// MEDIUM 3): a static import shipped the whole board + adapter + seed JSON in
// the main bundle for every user regardless of the scheduling.ai flag.
const InboxDashboard = lazyWithRetry(
  () => import('@/scheduling/inbox/inbox-dashboard-gate').then((m) => ({ default: m.InboxDashboard })),
  'inbox',
);
const ReportsRoutes = lazyWithRetry(() => import('@/reports/reports-routes'), 'reports');
const SettingsRoutes = lazyWithRetry(
  () => import('@/settings').then((m) => ({ default: m.SettingsRoutes })),
  'settings',
);
const PatientsRoutes = lazyWithRetry(() => import('@/patients/patients-routes'), 'patients');
const StockRoutes = lazyWithRetry(() => import('@/stock/stock-routes'), 'stock');
const StudioRoutes = lazyWithRetry(() => import('@/studio/studio-routes'), 'studio');
const ViewAppointmentPanelPage = lazyWithRetry(
  () =>
    import('@/scheduling/pages/view-appointment-panel-page').then((m) => ({
      default: m.ViewAppointmentPanelPage,
    })),
  'view-appointment-panel-page',
);

// `NavId` is now sourced from `@/app/lib/last-selected-nav` (single source of
// truth shared with the persistence allow-list — Samer review PR #512 §5).

export default function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const isBillingRoute = location.pathname.startsWith('/billing');
  const isReportsRoute = location.pathname.startsWith('/reports');
  const isSettingsRoute = location.pathname.startsWith('/settings');
  const isStockRoute = location.pathname.startsWith('/stock');
  const isStudioRoute = location.pathname.startsWith('/studio');
  // Patients is derived from the URL (like billing/reports/stock/studio) so a
  // cold-load / refresh / bookmark of a `/patients*` URL (e.g. an edit deep
  // link, `/patients/edit/:id`) mounts PatientsRoutes instead of falling
  // through to `stateNav` (default 'calendar') and bouncing to the Calendar.
  const isPatientsRoute = location.pathname.startsWith('/patients');
  // The Dashboard has its own address (Antonin, 12 Oct), so every rail screen is a history entry.
  const isDashboardRoute = location.pathname.startsWith('/dashboard');
  const navigationType = useNavigationType();
  // Standalone Consent surface retired (PROD-1051 doc-forms) — /consent now redirects to
  // Studio, where the consent-form builder lives. Kept so old bookmarks don't 404.
  const isConsentRoute = location.pathname.startsWith('/consent');
  const isViewAppointmentRoute = location.pathname.startsWith('/scheduling/appointment/');
  // Staff chat (SR-14): the Team dock and the Dashboard's Team card exist only with `?x=staff-chat`.
  const { enabled: staffChat, isOldMessagesAddress } = useStaffChatShell(location.pathname);
  // Post-auth landing page = Calendar (PROD-926). Mirrors Flutter's
  // default home in `menu_container_screen.dart`. Previously defaulted
  // to 'patients' which only showed because the DEV_CONFIG bootstrap
  // silently authenticated the user; with bootstrap stripped, this is
  // the first surface a real authenticated user sees.
  const [stateNav, setStateNav] = useState<NavId>('calendar');

  // Force-logout SignalR listener (PROD-857). Subscribes to the Identity
  // hub `UpdateUser` event; on trigger for current user + PM app type,
  // shows the non-dismissable force-logout dialog. OK clears the session.
  // Flutter ref: `menu_container_screen.dart:238-271` (`initiateSignalR`).
  const [forceLogoutOpen, setForceLogoutOpen] = useState(false);
  const handleForceLogoutTrigger = useCallback(() => {
    setForceLogoutOpen(true);
  }, []);
  useForceLogout({ onForceLogout: handleForceLogoutTrigger });
  const handleForceLogoutConfirm = useCallback(() => {
    useAuthStore.getState().logout();
    setForceLogoutOpen(false);
  }, []);

  // Practice Setup wizard mount-gate (PROD-859 / epic PROD-856).
  //
  // Mirrors Flutter `menu_container_screen.dart:443-462`:
  //   case orgAdminRole:
  //     initialWidget = (loggedUserData?.isOnboarded ?? false)
  //         ? _menuSelectedContentsWidget(...)
  //         : PracticeSetupBaseScreen(...);
  //
  // Per CLAUDE.md role IDs: orgAdminRole = "1" (numeric string from the
  // backend's `user.role` field, NOT the JWT's `orgRoles` claim). The
  // wizard renders only for org-admins with isOnboarded falsey — every
  // other role and every onboarded admin sees the normal nav shell.
  //
  // `onComplete` is wired in PR 6 — it will write `isOnboarded = true`
  // to `auth-store` + persist to `localStorage` (Flutter parity: pure
  // client-side, no backend endpoint exists — verified via Flutter
  // source grep, see PROD-856 cross-cutting #8 finding #2).
  const user = useAuthStore((s) => s.user);
  const authStatus = useAuthStore((s) => s.status);
  const isWizardRequired = user?.role === ORG_ADMIN_ROLE && user?.isOnboarded !== true;

  // Unauthenticated-redirect guard. Bounces every unauthenticated session
  // to `/login` (Flutter `menu_container_screen.dart` routes to LoginScreen
  // on null user data). Fires in both dev and prod now that PROD-926 has
  // removed the DEV_CONFIG bootstrap that previously auto-logged the user
  // in on the first api-client call.
  const isUnauthenticated = authStatus === 'unauthenticated';

  // PROD-868 — Notification Bell badge wiring. Two effects compose:
  //   1. `useUnreadNotificationCount` — one-shot fetch on auth boot
  //   2. `useNotificationCountRealtime` — SignalR push patches the same
  //      cache key live; badge updates within one render of a server
  //      `NotificationCountChanged` event (filtered to current user)
  // PROD-869 — Bell click opens the NotificationPanel (Sheet mounted
  // below). The panel store's openPanel() resets seenDuringOpen and
  // triggers the ResetSeenCount mutation inside the panel.
  const unreadCountQuery = useUnreadNotificationCount();
  useNotificationCountRealtime();
  const notificationCount = unreadCountQuery.data ?? 0;
  // In-app WhatsApp new-message notification: toast + Inbox badge (VITE_INBOX_NOTIFY, default ON; see the hook).
  const inboxNotify = inboxNotifyEnabled();
  const inboxUnreadCount = useInboxUnreadTotal(inboxNotify);
  const openNotificationPanel = useNotificationPanelStore((s) => s.openPanel);

  // When the user opens the appointment-panel route, set stateNav to
  // 'calendar' so the back arrow's `navigate('/')` deterministically
  // lands on the scheduling page. Without this, a user who came in
  // via a direct URL (e.g. bookmark) would land on Patients after
  // pressing back.
  useEffect(() => {
    if (isViewAppointmentRoute && stateNav !== 'calendar') {
      setStateNav('calendar');
    }
  }, [isViewAppointmentRoute, stateNav]);

  // Cross-domain "Create Appointment" handoff (patient-list kebab). The app
  // shell hosts the calendar's CreateAppointmentPanel as an OVERLAY over the
  // current surface — we deliberately do NOT switch to the Calendar view
  // (product decision 2026-06-02): the user stays on the patient list, books,
  // sees the toast, and the overlay closes. The shell may import scheduling;
  // `patients/` may not, hence the shared store + overlay live here.
  const pendingCreateAppointment = useCreateAppointmentHandoff(
    (s) => s.pendingPrefill,
  );
  const clearCreateAppointment = useCreateAppointmentHandoff((s) => s.clear);
  // Set only by callers that asked to learn the new appointment id (Referrals desk, PROD-1395).
  const pendingProviderFilter = useCreateAppointmentHandoff((s) => s.pendingProviderFilter);
  const pendingOnBooked = useCreateAppointmentHandoff((s) => s.pendingOnBooked);
  const pendingOnBookedWithoutId = useCreateAppointmentHandoff((s) => s.pendingOnBookedWithoutId);

  const activeNav: NavId = isBillingRoute
    ? 'billing'
    : isReportsRoute
      ? 'reports'
      : isStockRoute
        ? 'stock'
        : isStudioRoute
          ? 'studio'
          : isPatientsRoute
            ? 'patients'
            : isDashboardRoute
              ? 'dashboard'
              : stateNav;

  // Last-selected-menu restore (Flutter `menu_container_screen.dart`
  // lastSelectedMenu). "Do as Flutter does" (Andrew 2026-06-08).
  //
  // PROD-926 is NOT overridden: Flutter `remove()`s the key on logout, and so
  // do we (`auth-store.logout()` → `clearLastNav()`). Every logout path
  // (manual, force-logout SignalR, session-expired) routes through logout(),
  // so a FRESH LOGIN always finds no key → falls back to Calendar exactly as
  // PROD-926 intends. Restore therefore only applies to a SESSION-RESUME
  // reload (browser refresh / app reopen with a still-valid token) — the
  // user returns to the surface they were on, matching Flutter.
  //
  // Restored ONCE on the initial authenticated render at '/'. scripsAdmin
  // ("0") never restores (Flutter parity); a deep-link (pathname !== '/') or
  // the onboarding wizard takes precedence. Calendar is the fallback.
  //
  // PROD-1776 — an explicit URL now also takes precedence AT '/'. Previously
  // only a non-'/' pathname counted as a deep link, so `/?date=…&view=Day`
  // pasted by a colleague rendered whatever their last-selected menu was and
  // silently dropped the params. React (unlike Flutter) exposes calendar
  // state in the URL, and the 2026-07-28 parity sweep's repro links all
  // assume that works from a cold open. Option 1 of the ticket: URL wins when
  // explicit params are present, restore still owns the bare-'/' case.
  const [navRestored, setNavRestored] = useState(false);
  useEffect(() => {
    if (navRestored) return;
    // Not authenticated / mid-onboarding: bail WITHOUT latching, so the
    // restore still gets its one shot once those resolve.
    if (authStatus !== 'authenticated') return;
    if (isWizardRequired) return; // onboarding wins; retry after it completes
    if (
      shouldRestoreLastNav({
        authStatus,
        isWizardRequired,
        role: user?.role,
        pathname: location.pathname,
        search: location.search,
        scripsAdminRole: SCRIPS_ADMIN_ROLE,
      })
    ) {
      const last = readLastNav();
      // Role confinement (PROD-2396): never restore a confined user onto a
      // surface their rail no longer offers. A Technician can only have
      // persisted 'calendar' through the rail, but a key written before a
      // role change would otherwise strand them on Patients with one button
      // out. `allowsNavId` is true for every unconfined role, so this is a
      // no-op for everyone else.
      if (last && last !== 'calendar' && allowsNavId(user, last)) {
        if (
          last === 'billing' ||
          last === 'reports' ||
          last === 'stock' ||
          last === 'studio'
        ) {
          navigate(`/${last}`);
        } else {
          setStateNav(last); // 'patients' | 'dashboard'
        }
      }
    }
    setNavRestored(true);
  }, [
    navRestored,
    authStatus,
    isWizardRequired,
    user,
    location.pathname,
    location.search,
    navigate,
  ]);

  // PROD-1776 — keep `hasRootDeepLink` honest by clearing the calendar's
  // params once the user is no longer on the calendar.
  //
  // The calendar URL-syncs `?date=&view=` on every render, and those params
  // survive an in-app switch to Patients / Dashboard because both surfaces
  // share the '/' pathname. Left in place, the address bar would describe a
  // screen that isn't showing, and — worse — the next reload would read the
  // leftovers as a deep link and skip the restore, defeating it for the most
  // common resume path (calendar → patients → refresh). Stripping on leave is
  // also the hygiene the ticket's option 2 asks for.
  //
  // No loop: SchedulingPage is unmounted whenever activeNav !== 'calendar'
  // (see the render switch below), so nothing rewrites the params afterwards.
  // Losing the previously-viewed day is intended, not collateral — the
  // scheduling store documents `selectedDate`/`activeView` as reset-on-load
  // Flutter parity (`scheduling-store.ts:17-18`).
  useEffect(() => {
    if (location.pathname !== '/') return;
    if (activeNav === 'calendar') return;
    if (!hasRootDeepLink(location.search)) return;
    const rest = stripRootDeepLinkParams(location.search);
    // Keeps the entry's state: it records which screen the entry is (Back / Forward read it).
    navigate({ pathname: '/', search: rest }, { replace: true, state: location.state });
  }, [location.pathname, location.search, activeNav, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist the active nav so the restore above has something to read next
  // session. Gated on `navRestored` so the initial-mount write (activeNav
  // defaults to 'calendar') can't clobber the stored value before restore
  // reads it. Settings (not a NavId) is skipped by writeLastNav's allow-list.
  //
  // Why `navRestored` is STATE, not a useRef (Samer review PR #512 §2):
  // the persist effect must NOT run until restore has both read the stored
  // value AND applied its setStateNav. `navRestored` flips true only inside
  // the restore effect, AFTER readLastNav() + setStateNav() — and because
  // those are batched, the next render carries navRestored=true together
  // with the restored activeNav, so this effect writes the RESTORED value,
  // never 'calendar'. A useRef set synchronously would reintroduce the
  // clobber: persist would run on the same render where activeNav is still
  // 'calendar' (setStateNav not yet committed) and overwrite the stored
  // value. The re-render that state forces is exactly what avoids the race,
  // including under StrictMode's double-invoke (idempotent: restore re-reads
  // the same value, persist still gated until the flag commits).
  useEffect(() => {
    if (!navRestored) return;
    writeLastNav(activeNav);
  }, [navRestored, activeNav]);

  // Shell-level unsaved-changes guard. When a surface (patient Add/Edit) has
  // dirty edits it calls `setBlocking(true)`; `runGuarded` then defers the
  // SideNav navigation behind the discard confirm instead of silently
  // dropping the edits. Off the edit page `blocking` is false and `runGuarded`
  // runs the nav synchronously — zero behaviour change.
  const runGuarded = useNavGuard((s) => s.run);
  const handleNav = (id: NavId) => {
    runGuarded(() => {
      if (id === 'billing') {
        navigate('/billing');
        return;
      }
      if (id === 'reports') {
        navigate('/reports');
        return;
      }
      if (id === 'stock') {
        navigate('/stock');
        return;
      }
      if (id === 'studio') {
        navigate('/studio');
        return;
      }
      // calendar / patients / dashboard are state-nav targets that render only
      // when the URL is '/'. If we're on ANY URL-derived route (/patients,
      // /billing, /settings, /scheduling/appointment/:id, …) the URL flag masks
      // the state-nav target — so reset to '/' first. The old list-of-flags
      // guard omitted `isPatientsRoute`, which made Patients (/patients) →
      // Calendar silently no-op until the user detoured through another tab.
      // Every rail switch is a browser history step (Antonin, 12 Oct, the practitioner app's rule): Back
      // and Forward walk the screens. Patients and Dashboard have their own address; the Calendar is '/'.
      // Their screen comes from the address alone: setting stateNav here too ran the Calendar's address
      // clean-up one render early and it replaced /dashboard with '/'.
      if (id === 'patients') { navigate('/patients'); return; }
      if (id === 'dashboard') { navigate('/dashboard'); return; }
      navigate('/');
      setStateNav(id);
    });
  };
  // The toast's Open: ask the inbox for that thread, then go there (behind the unsaved-changes guard).
  const openInboxThreadFromToast = useCallback(
    (conversationId: string) =>
      runGuarded(() => {
        requestInboxThread(conversationId);
        navigate(inboxThreadPath(conversationId));
      }),
    [runGuarded, navigate],
  );
  useInboxNewMessageNotify({ enabled: inboxNotify, onOpen: openInboxThreadFromToast });
  // Back / Forward onto '/': that entry is the Calendar.
  useEffect(() => {
    if (navigationType === 'POP' && location.pathname === '/') setStateNav('calendar');
  }, [location.key]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isUnauthenticated) {
    // PROD-2366 — the return target is the WHOLE location (pathname + search
    // + hash), not just the pathname. See `auth/return-to.ts` for why the
    // fix is a verbatim round trip rather than one more carried key.
    return <LoginRedirect />;
  }

  if (isWizardRequired) {
    return (
      <>
        <OnboardingWizard
          firstName={user?.firstName ?? null}
          onComplete={() => {
            // PROD-930 (PR 6) — delegate to the store action so the
            // mutation path stays single + testable. Flutter parity:
            // `menu_container_screen.dart:451-457` (purely local,
            // no backend write).
            useAuthStore.getState().markOnboardingComplete();
          }}
        />
        {/* Force-logout dialog must remain reachable during onboarding —
            a SignalR UpdateUser event mid-wizard should still surface. */}
        <ForceLogoutDialog
          open={forceLogoutOpen}
          onConfirm={handleForceLogoutConfirm}
        />
      </>
    );
  }

  // Role confinement route guard (PROD-2396). The rail filter above removes
  // the LINKS to the modules a confined role has no business in; this removes
  // the ROUTES. Without it a Technician still reaches /patients, /billing,
  // /stock, /reports, /studio or a /scheduling/appointment/:id deep link by
  // typing, bookmarking, or letting the browser reopen yesterday's tab — and
  // a stale bookmark surviving a role change is the realistic case, not a
  // malicious one. It takes the whole `location`, not just the pathname,
  // because `/?actor=devices` is a different surface from `/` (PROD-2398). Flutter gets away with menu-only filtering because its
  // routes are menu-driven; React's are URL-driven, so the rail alone leaves
  // the feature half-built.
  //
  // The whole policy (allowlist, destination, and why there is no "access
  // denied" screen) lives in `confinedRedirectPath`. It returns null for
  // every unconfined role before it even looks at the pathname, so no other
  // role can be affected by it.
  //
  // Placed AFTER the unauthenticated and onboarding returns so neither is
  // pre-empted, and after every hook so the hook order is unconditional.
  //
  // Again: UX confinement, not a security boundary. The APIs behind these
  // routes enforce no role (PROD-2388).
  const confinedRedirect = confinedRedirectPath(location, user);
  if (confinedRedirect) {
    return <Navigate to={confinedRedirect} replace />;
  }

  return (
    // Zone contract shell: the rail (Z1) and the top bar (Z2) sit on the app
    // grey with no fill of their own; page content lives in the raised white
    // WorkspaceFrame (Z3 — inset 0 left, 12 right and bottom, radius 12,
    // hairline). Pages keep their own scroll inside the frame; <main> is not
    // the scroller.
    <div className="flex h-screen overflow-hidden bg-[var(--surface-app)]">
      <SideNav
        activeId={railActiveId(location.pathname, activeNav, location.search)}
        onNavigate={handleNav}
        notificationCount={notificationCount}
        inboxUnreadCount={inboxUnreadCount}
        onBellClick={openNotificationPanel}
      />
      <div className="flex min-w-0 flex-1 flex-col">
      <TopBar
        activeNav={activeNav}
        onNavigate={(path, options) => runGuarded(() => navigate(path, options))}
        onBack={() => runGuarded(() => navigate(-1))}
      />
      <main className="mr-[var(--zone-frame-inset-right)] mb-[var(--zone-frame-inset-bottom)] min-h-0 flex-1 overflow-hidden rounded-[var(--zone-frame-radius)] border border-[var(--surface-line)] bg-[var(--surface-read)]">
        {/* Keyed by pathname so the boundary resets when the user
            navigates away from a route that failed to load. */}
        <RouteErrorBoundary key={location.pathname}>
        {isOldMessagesAddress ? (
          // The retired /messages page: the Dashboard, keeping ?thread=<id> (the dock opens on it).
          <Navigate to={`/dashboard${location.search}`} replace />
        ) : isBillingRoute ? (
          <Suspense fallback={<RouteFallback label="billing" />}>
            <BillingRoutes />
          </Suspense>
        ) : isStockRoute ? (
          <Suspense fallback={<RouteFallback label="stock" />}>
            <StockRoutes />
          </Suspense>
        ) : isStudioRoute ? (
          <Suspense fallback={<RouteFallback label="studio" />}>
            <StudioRoutes />
          </Suspense>
        ) : isConsentRoute ? (
          // Retired standalone surface — redirect old /consent links to Studio.
          <Navigate to="/studio" replace />
        ) : isReportsRoute ? (
          <Suspense fallback={<RouteFallback label="reports" />}>
            <ReportsRoutes />
          </Suspense>
        ) : isSettingsRoute ? (
          <Suspense fallback={<RouteFallback label="settings" />}>
            <SettingsRoutes />
          </Suspense>
        ) : isViewAppointmentRoute ? (
          <Suspense fallback={<RouteFallback label="appointment" />}>
            {/* Wrap in <Routes> so :appointmentId binds to useParams()
                inside ViewAppointmentPanelPage. Without a matching
                <Route path> the URL parameter is invisible to the
                consumer hook. */}
            <Routes>
              <Route
                path="/scheduling/appointment/:appointmentId"
                element={<ViewAppointmentPanelPage />}
              />
            </Routes>
          </Suspense>
        ) : activeNav === 'calendar' ? (
          <SchedulingPage />
        ) : activeNav === 'patients' ? (
          <Suspense fallback={<RouteFallback label="patients" />}>
            <PatientsRoutes />
          </Suspense>
        ) : activeNav === 'dashboard' ? (
          // PROD-1848 — WhatsApp inbox behind VITE_SCHEDULING_AI. Fail-closed:
          // flag off renders the EXACT same ComingSoon placeholder as before
          // (passed through untouched); flag on mounts the inbox.
          <Suspense fallback={<RouteFallback label="dashboard" />}>
            {/* Staff chat (SR-14, `?x=staff-chat`): the Team card leads the Dashboard. Off: unchanged. */}
            {staffChat ? (
              <div className="flex h-full min-h-0 flex-col">
                <div className="px-4 pt-4">
                  {/* Its own Suspense and error boundary: a slow or failing card never hides the inbox below. */}
                  <StaffChatDashboardSlot />
                </div>
                <div className="min-h-0 flex-1">
                  <InboxDashboard
                    fallback={<ComingSoon label={activeNav} />}
                    onOpenPatient={(patientId) => navigate(`/patients/edit/${patientId}?tab=chart`, { state: { from: '/dashboard' } })}
                  />
                </div>
              </div>
            ) : (
              <InboxDashboard
                fallback={<ComingSoon label={activeNav} />}
                // The thread's "Open patient": the chart, with the patient page's Back returning to the inbox.
                onOpenPatient={(patientId) => navigate(`/patients/edit/${patientId}?tab=chart`, { state: { from: '/dashboard' } })}
              />
            )}
          </Suspense>
        ) : (
          <ComingSoon label={activeNav} />
        )}
        </RouteErrorBoundary>
      </main>
      </div>
      <ForceLogoutDialog
        open={forceLogoutOpen}
        onConfirm={handleForceLogoutConfirm}
      />
      {/* Shell-level unsaved-changes confirm — surfaces when a SideNav /
          profile-menu navigation tries to leave the patient Add/Edit page
          with dirty edits (the in-page Back button has its own dialog). */}
      <UnsavedChangesGuardDialog />
      {/*
        * Not mounted for a confined role (PROD-2397). The bell that opens it
        * is already gone from the rail, but that alone would not be enough:
        * `useUserNotifications` runs on mount with no `enabled` guard, so a
        * mounted-but-unreachable panel still fetches the notification list —
        * patient-identifying text — into the React Query cache for someone
        * who can never open it. `&&` short-circuits to `false`, which React
        * skips outright, so the hook never runs and no request fires. Same
        * hard-unmount reasoning as the worklist and Order Requests tabs.
        *
        * The unread COUNT query above still runs: it is a hook, so it cannot
        * be called conditionally, and it returns an integer rather than PHI.
        * Its badge has nowhere to render.
        */}
      {!isNavConfined(user) && <NotificationPanel />}
      {/* Staff chat (SR-14, `?x=staff-chat`): the Team dock, bottom right of every page, mounted once. */}
      {staffChat && <StaffChatDockSlot />}
      <IconButtonTooltips />

      {/* Create Appointment overlay (patient-list kebab handoff). Renders the
          calendar's CreateAppointmentPanel as a left drawer OVER the current
          surface — no nav switch. Booking shows its own toast + calls
          onClose, which clears the store and dismisses the overlay; the user
          stays where they were (e.g. the patient list). */}
      {pendingCreateAppointment ? (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/10"
            aria-hidden
            onClick={clearCreateAppointment}
          />
          {/* Opens from the right edge (Antonin, 13 Oct), so it never covers the rail, open or folded. */}
          <div className="fixed inset-y-0 right-0 z-50 flex">
            <CreateAppointmentPanel
              open
              edge="right"
              onClose={clearCreateAppointment}
              mode="create"
              organizationId={user?.organizationId ?? ''}
              practiceId={user?.practiceId ?? ''}
              userId={user?.userId ?? ''}
              onSearchPatients={noopSearchPatients}
              initialValues={pendingCreateAppointment}
              providerFilter={pendingProviderFilter ?? undefined}
              onSaveSuccess={bookedToSaveSuccess(pendingOnBooked, pendingOnBookedWithoutId)}
            />
          </div>
        </>
      ) : null}
    </div>
  );
}

/** Patient search is unused here — the kebab handoff always presets the
 *  patient. Matches scheduling-page's noop until the search endpoint lands. */
const noopSearchPatients = async () => [];

function RouteFallback({ label }: { label: string }) {
  return (
    <div className="flex items-center justify-center h-full text-text-muted text-sm">
      Loading {label}…
    </div>
  );
}

function ComingSoon({ label }: { label: string }) {
  return (
    <div className="flex items-center justify-center h-full text-text-muted text-sm">
      {label.charAt(0).toUpperCase() + label.slice(1)} — coming soon
    </div>
  );
}
